import React, { useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { metaApi } from '../api/endpoints';
import { toast } from 'sonner';

const KEY_LABELS = {
  META_APP_ID: 'App ID',
  META_APP_SECRET: 'App Secret',
  META_PAGE_ID: 'Page ID',
  META_PAGE_ACCESS_TOKEN: 'Page Access Token',
  META_AD_ACCOUNT_ID: 'Ad Account ID',
};

export const MetaIntegrationCard = () => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    metaApi.status().then(setStatus).catch(e => console.error('Meta status fetch failed', e));
  }, []);

  if (!status) return null;
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}${status.webhook_path}`;
  const copy = (v, label) => { navigator.clipboard.writeText(v); toast.success(`${label} copied`); };

  return (
    <Card className="p-4 border-slate-200" data-testid="meta-integration-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center">
            <Icons.Facebook className="w-5 h-5" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800">Meta Lead Ads (Facebook / Instagram)</div>
            <div className="text-[11px] text-slate-500">Auto-imports campaign leads in real time via webhook</div>
          </div>
        </div>
        <Badge className={status.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'} data-testid="meta-status-badge">
          {status.configured ? 'Connected' : 'Awaiting credentials'}
        </Badge>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-3 text-xs">
        <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 space-y-1.5">
          <div className="font-semibold text-slate-600 uppercase text-[10px]">Paste into Meta → Webhooks</div>
          <div className="flex items-center gap-1.5">
            <code className="bg-white border rounded px-1.5 py-0.5 truncate flex-1 text-[10px]">{webhookUrl}</code>
            <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => copy(webhookUrl, 'Webhook URL')} data-testid="meta-copy-webhook">
              <Icons.Copy className="w-3 h-3" />
            </Button>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500 shrink-0">Verify token:</span>
            <code className="bg-white border rounded px-1.5 py-0.5 truncate flex-1 text-[10px]">{status.verify_token || '—'}</code>
            <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => copy(status.verify_token, 'Verify token')} data-testid="meta-copy-token">
              <Icons.Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
          <div className="font-semibold text-slate-600 uppercase text-[10px] mb-1.5">Credentials</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(KEY_LABELS).map(([k, label]) => (
              <Badge key={k} variant="outline" className={status.credentials[k] ? 'border-emerald-300 text-emerald-700 text-[10px]' : 'border-amber-300 text-amber-700 text-[10px]'}>
                {status.credentials[k] ? '✓' : '✗'} {label}
              </Badge>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-2">
            Imported: <strong>{status.leads_imported}</strong> leads
            {status.pending_events > 0 && <> · <strong className="text-amber-700">{status.pending_events} pending</strong></>}
          </div>
          {status.configured && status.pending_events > 0 && (
            <Button size="sm" variant="outline" className="h-6 text-[10px] mt-1.5" data-testid="meta-retry-pending"
              onClick={async () => {
                try { const r = await metaApi.retryPending(); toast.success(`Re-queued ${r.queued} pending leads`); }
                catch (e) { toast.error(e?.response?.data?.detail || 'Retry failed'); }
              }}>
              Retry pending imports
            </Button>
          )}
        </div>
      </div>
      {!status.configured && (
        <div className="text-[11px] text-slate-500 mt-2.5">
          To go live: create a Business app at developers.facebook.com → add the credentials above to the backend environment → subscribe the webhook to your Page's <code>leadgen</code> events. Leads received before connection are stored and can be imported with one click after.
        </div>
      )}
    </Card>
  );
};

export default MetaIntegrationCard;
